import { CommonModule } from "@angular/common";
import { Component, DestroyRef, ViewChild, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { ButtonCellRenderer } from "@app/shared/components/tables/ag-grid-cells/button-cell-renderer/button-cell-renderer.component";
import { personFirstNames, personLastName, personLastNameKey } from "@app/shared/util/person-name";
import { EzFormComponent } from "@template-ui/components/ez-form/ez-form.component";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { ButtonConfig } from "@template-ui/types";
import { EzFormInputType, InputConfig } from "@template-ui/types/ez-form.types";
import { ColDef } from "ag-grid-community";
import { catchError, of } from "rxjs";
import {
  DEFAULT_HEARING_SCHEDULE_SORT,
  HearingScheduleDisplayError,
  NEEDS_HEARING_INPUT_HINT,
  NEEDS_HEARING_INPUT_LABEL,
  ProceedingCalendarRow,
  formatResource,
  mapHearingScheduleError,
  needsHearingInput,
  sortHearings,
} from "../hearing-schedule/hearing-schedule.models";
import { HearingScheduleService } from "../hearing-schedule/hearing-schedule.service";
import { hearingPhaseCommands } from "../navigation/phms-detail-navigation";
import { PhmsDetailNavigationService } from "../navigation/phms-detail-navigation.service";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";
import { PHMS_HEARING_TERMINOLOGY } from "../phms-hearing.terminology";
import { PHMS_OPERATIONAL_TERMINOLOGY } from "../phms-operational.terminology";

/** Hearing type is not carried on the calendar list projection; provisional until 912 supplies it. */
const HEARING_TYPE_PLACEHOLDER = "—";

interface HearingListFilters {
  status: string;
}

const EMPTY_HEARING_LIST_FILTERS: HearingListFilters = { status: "" };

/**
 * Read-only PHMS hearing list. A flat, query-backed EzGrid over the proceeding-calendar projection
 * (distinct from the calendar-first hearing schedule) with a server-side status filter, a
 * deterministic default sort (date/time ascending), and row drill-in to the hearing detail route.
 */
@Component({
  selector: "phms-hearing-list",
  standalone: true,
  imports: [CommonModule, UiLibraryModule],
  templateUrl: "./hearing-list.component.html",
  styleUrl: "./hearing-list.component.scss",
})
export class HearingListComponent {
  readonly hearingText = createTerminologyUsageSignals(PHMS_HEARING_TERMINOLOGY.hearingList);
  readonly operationalText = createTerminologyUsageSignals(PHMS_OPERATIONAL_TERMINOLOGY.hearingList);
  private readonly destroyRef = inject(DestroyRef);
  private readonly scheduleService = inject(HearingScheduleService);
  private readonly router = inject(Router);
  private readonly detailNavigation = inject(PhmsDetailNavigationService);

  @ViewChild("filterForm")
  private filterForm?: EzFormComponent;

  readonly isLoading = signal(true);
  readonly errorState = signal<HearingScheduleDisplayError | null>(null);
  readonly hearings = signal<ProceedingCalendarRow[]>([]);
  readonly filters = signal<HearingListFilters>({
    ...EMPTY_HEARING_LIST_FILTERS,
  });

  readonly visibleHearings = computed<ProceedingCalendarRow[]>(() => sortHearings(this.hearings(), DEFAULT_HEARING_SCHEDULE_SORT));
  readonly hasHearings = () => this.visibleHearings().length > 0;

  readonly columnDefs = computed<ColDef[]>(() => this.buildColumnDefs());
  get filterFormConfig(): InputConfig[] {
    return [
      {
        name: "status",
        type: EzFormInputType.TEXT,
        label: this.operationalText.statusFilter(),
        row: 1,
        width: "2x",
        validation: [Validators.maxLength(60)],
      },
    ];
  }

  readonly filterActionButtons: ButtonConfig[] = [
    { id: "applyFilters", buttonText: "Apply" },
    { id: "clearFilters", buttonText: "Clear", severity: "secondary" },
  ];

  filterFormValues: HearingListFilters = { ...EMPTY_HEARING_LIST_FILTERS };

  readonly pageTitle = this.hearingText.title;
  readonly pageSubtitle = this.hearingText.description;

  readonly formatResource = formatResource;

  constructor() {
    this.load();
  }

  onFilterActionButtonClick(button: ButtonConfig): void {
    if (button.id === "applyFilters") {
      this.applyFilters();
    } else if (button.id === "clearFilters") {
      this.clearFilters();
    }
  }

  applyFilters(): void {
    this.filters.set({ ...this.filterFormValues });
    this.load();
  }

  clearFilters(): void {
    this.filterFormValues = { ...EMPTY_HEARING_LIST_FILTERS };
    this.filters.set({ ...EMPTY_HEARING_LIST_FILTERS });
    this.filterForm?.clearErrors();
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    this.errorState.set(null);
    this.hearings.set([]);

    const status = this.filters().status.trim();
    this.scheduleService
      .getCalendar(status ? { statusCode: status } : {})
      .pipe(
        catchError((error: unknown) => {
          this.errorState.set(mapHearingScheduleError(error));
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((rows) => {
        this.hearings.set(rows ?? []);
        this.isLoading.set(false);
      });
  }

  openHearing(row: ProceedingCalendarRow): void {
    if (!row) {
      return;
    }
    this.detailNavigation.openHearingDetail(row.scheduledProceedingId, {
      url: this.router.url,
      label: this.pageTitle(),
    });
  }

  /**
   * Overdue-row CTA (#1447 AC3): clicking the status cell of a hearing that is awaiting its input
   * deep-links to the at-hearing input surface (`/phms/hearing-detail/{id}/at-hearing`), where
   * recording the outcome finalizes the capture and clears the derived overdue flag everywhere
   * (docs/phms/hearing-past-date-lifecycle.md). Non-flagged rows keep the ordinary Open drill-in.
   */
  openHearingInput(row: ProceedingCalendarRow): void {
    if (!row || !(row.scheduledProceedingId > 0)) {
      return;
    }
    void this.router.navigate(hearingPhaseCommands(row.scheduledProceedingId, "at"));
  }

  formatStatus(statusCode: string | null | undefined): string {
    const normalized = (statusCode ?? "").trim();
    if (!normalized) {
      return "Unknown";
    }
    return normalized
      .split(/[_\-\s]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  }

  private buildColumnDefs(): ColDef[] {
    return [
      {
        field: "proceedingNumber",
        headerName: this.hearingText.header(),
        width: 150,
        sortable: true,
      },
      {
        field: "title",
        headerName: "Last Name",
        flex: 0.8,
        minWidth: 130,
        sortable: true,
        // Renders the last name and sorts on the last-name key (#1436); the row keeps the raw title.
        valueFormatter: ({ value }) => personLastName(value as string | null | undefined),
        comparator: (left, right) =>
          personLastNameKey(left as string | null | undefined).localeCompare(personLastNameKey(right as string | null | undefined)),
      },
      {
        headerName: "First Name",
        flex: 0.8,
        minWidth: 120,
        sortable: true,
        // Derived "First [Middle]" view of the same raw title.
        valueGetter: ({ data }) => personFirstNames((data as ProceedingCalendarRow | undefined)?.title),
      },
      {
        headerName: this.hearingText.type(),
        width: 150,
        sortable: false,
        valueGetter: () => HEARING_TYPE_PLACEHOLDER,
      },
      {
        field: "scheduledStartUtc",
        headerName: "Scheduled Date / Time",
        flex: 1,
        minWidth: 190,
        sortable: true,
        sort: "asc",
        valueFormatter: ({ value }) => formatDateTimeLabel(value as string | null | undefined),
      },
      {
        headerName: "Location / Resource",
        flex: 1,
        minWidth: 180,
        sortable: true,
        valueGetter: ({ data }) => formatLocationResource(data as ProceedingCalendarRow),
      },
      {
        field: "schedulingStatusCode",
        headerName: this.operationalText.statusColumn(),
        width: 220,
        sortable: true,
        valueFormatter: ({ value }) => this.formatStatus(value as string | null | undefined),
        // Overdue past-date flag (#1447 AC2): an amber "Needs hearing input" chip renders beside the
        // status when the hearing's window elapsed without a finalized capture (server-derived).
        cellRenderer: ({ data, value }: { data?: ProceedingCalendarRow; value?: unknown }) =>
          renderStatusWithNeedsInputChip(this.formatStatus(value as string | null | undefined), data),
        // Clicking the flagged cell routes to the hearing-input capture surface (#1447 AC3).
        onCellClicked: ({ data }) => {
          const row = data as ProceedingCalendarRow | undefined;
          if (row && needsHearingInput(row)) {
            this.openHearingInput(row);
          }
        },
      },
      {
        headerName: "",
        width: 90,
        maxWidth: 90,
        sortable: false,
        cellRenderer: ButtonCellRenderer,
        cellRendererParams: {
          label: "Open",
          onClick: (params: { rowData: ProceedingCalendarRow }) => this.openHearing(params.rowData),
        },
      },
    ];
  }
}

/** Escapes display text before embedding it in a cell-renderer HTML string. */
function escapeHtml(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

/**
 * Status cell content (#1447): the humanized status, plus the amber "Needs hearing input" chip when
 * the row's scheduled window elapsed without a finalized hearing capture. The chip carries the
 * shared hint as its tooltip; the cell's click handler routes to the capture surface.
 */
function renderStatusWithNeedsInputChip(statusLabel: string, row: ProceedingCalendarRow | undefined): string {
  const status = `<span class="status-text">${escapeHtml(statusLabel)}</span>`;
  if (!needsHearingInput(row)) {
    return status;
  }
  return `${status} <span class="needs-input-chip" title="${escapeHtml(NEEDS_HEARING_INPUT_HINT)}">${escapeHtml(
    NEEDS_HEARING_INPUT_LABEL,
  )}</span>`;
}

function formatLocationResource(row: ProceedingCalendarRow): string {
  const location = row.locationName?.trim();
  const resource = formatResource(row);
  if (location && resource && resource !== "—") {
    return `${location} / ${resource}`;
  }
  return location || (resource !== "—" ? resource : "—");
}

function formatDateTimeLabel(value: string | null | undefined): string {
  if (!value) {
    return "Unscheduled";
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(date);
}
